import { createSlice, createAsyncThunk, createEntityAdapter, createSelector } from '@reduxjs/toolkit'
import { NoteService } from 'services/crm'

const notesAdapter = createEntityAdapter({
  sortComparer: (a, b) => (a.createdAt < b.createdAt ? 1 : -1),
})

const cacheKey = (params = {}) =>
  params.dealId != null
    ? `deal:${params.dealId}`
    : params.leadId != null
    ? `lead:${params.leadId}`
    : 'all'

export const fetchNotes = createAsyncThunk(
  'crm/notes/fetchAll',
  async (params, { rejectWithValue }) => {
    try {
      const res = await NoteService.getAll(params)
      return { items: res.data, key: cacheKey(params) }
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error fetching notes')
    }
  },
  {
    condition: (params, { getState }) => {
      const state = getState().crmNotes
      return !state.loadedKeys || !state.loadedKeys.includes(cacheKey(params))
    },
  },
)

export const createNote = createAsyncThunk(
  'crm/notes/create',
  async (data, { rejectWithValue }) => {
    try {
      const res = await NoteService.create(data)
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error creating note')
    }
  },
)

export const updateNote = createAsyncThunk(
  'crm/notes/update',
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const res = await NoteService.update(id, data)
      return res.data
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error updating note')
    }
  },
)

export const removeNote = createAsyncThunk(
  'crm/notes/remove',
  async (id, { rejectWithValue }) => {
    try {
      await NoteService.remove(id)
      return id
    } catch (e) {
      return rejectWithValue(e.response?.data?.message || 'Error deleting note')
    }
  },
)

const initialState = notesAdapter.getInitialState({
  loading: 'idle',
  loadedKeys: [],
})

const notesSlice = createSlice({
  name: 'crmNotes',
  initialState,
  reducers: {
    invalidateNotes: (state) => {
      state.loadedKeys = []
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotes.fulfilled, (state, action) => {
        const { items, key } = action.payload
        notesAdapter.upsertMany(state, items)
        if (!state.loadedKeys.includes(key)) state.loadedKeys.push(key)
      })
      .addCase(createNote.fulfilled, (state, action) => {
        notesAdapter.upsertOne(state, action.payload)
      })
      .addCase(updateNote.fulfilled, (state, action) => {
        notesAdapter.upsertOne(state, action.payload)
      })
      .addCase(removeNote.fulfilled, (state, action) => {
        notesAdapter.removeOne(state, action.payload)
      })
  },
})

export const { invalidateNotes } = notesSlice.actions

const selectors = notesAdapter.getSelectors((state) => state.crmNotes)
export const selectAllNotes = selectors.selectAll
export const selectNoteById = selectors.selectById

export const selectNotesByDeal = (dealId) =>
  createSelector([selectAllNotes], (notes) => notes.filter((n) => n.dealId === dealId))

export const selectNotesByLead = (leadId) =>
  createSelector([selectAllNotes], (notes) => notes.filter((n) => n.leadId === leadId))

export default notesSlice.reducer
